import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { Box, Button, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Images } from "../../assets/common";
import AddPopup from "../AddPopup";
import sikibotLogo from "../../assets/common/resources/Sikibot.png";
import SubscriptionPopup from "../SubscriptionPopup";
import LeftBarUpload from "./LeftBarUpload"; // Import the upload component
// Add Material UI icon for Premium
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import LockIcon from '@mui/icons-material/Lock';
import PremiumBadgeIcon from "../../assets/common/icons/premium-badge.png";
import "./index.scss";
import GoLiveButton from './GoLive_List';
import ApiService from "../../services/api-service";
import { getUserId } from "../../constants/function";
import notifier from "../Notifier";


export default function LeftBar({ isNavOpen }) {
  const history = useHistory();
  const { t } = useTranslation("common");
  const [activeIndex, setActiveIndex] = useState(0);
  const [showPremiumPopup, setShowPremiumPopup] = useState(false);
  const [userSubscription, setUserSubscription] = useState(null);
  const [checkingSubscription, setCheckingSubscription] = useState(false);

  // Define your menu items in the desired order
  const menuItems = [
    {
      index: 0,
      path: "/app/Home",
      label: t("sidebar.home", { defaultValue: "Home" }),
      image: Images.home_default,
      activeImage: Images.home_enter,
    },
    {
      index: 1,
      path: "/app/Library",
      label: t("sidebar.library", { defaultValue: "Library" }),
      image: Images.library_default,
      activeImage: Images.library_enter,
    },
    {
      index: 2,
      path: "/app/Feed",
      label: t("sidebar.feed", { defaultValue: "Feed" }),
      image: Images.feed_default,
      activeImage: Images.feed_enter,
    },
    {
      index: 3,
      path: "/app/Subscription",
      label: t("sidebar.subscription", { defaultValue: "Subscription" }),
      image: Images.subs_default,
      activeImage: Images.subs_enter,
    },
    {
      index: 4,
      path: null, // No path since it opens a popup
      label: "Premium", // Simplified label
      isPremium: true, // Special flag for premium
      // No image properties for premium, we'll use the icon
    },
    {
      index: 99,
      path: "/app/ai-chat",
      label: t("sidebar.ai", { defaultValue: "Siki AI" }),
      image: sikibotLogo,
      activeImage: sikibotLogo,
      requiresSubscription: true, // Add subscription requirement
    },
  ];

  useEffect(() => {
    const determineActiveIndex = (path) => {
      const item = menuItems.find((menuItem) => menuItem.path && path.startsWith(menuItem.path));
      return item ? item.index : 0;
    };
    setActiveIndex(determineActiveIndex(history.location.pathname));
    // eslint-disable-next-line
  }, [history.location.pathname]);

  // Check subscription status on mount
  useEffect(() => {
    const checkUserSubscription = async () => {
      const userId = getUserId();
      if (userId) {
        setCheckingSubscription(true);
        try {
          const response = await ApiService.GET_SUBSCRIPTIONS(userId);
          setUserSubscription(response.result && response.result.length > 0);
        } catch (error) {
          console.error("Error checking subscription:", error);
          setUserSubscription(false);
        } finally {
          setCheckingSubscription(false);
        }
      }
    };
    checkUserSubscription();
  }, []);

  const handleMenuClick = async (item) => {
    if (item.isPremium) {
      // Open premium popup instead of navigating
      setShowPremiumPopup(true);
    } else if (item.requiresSubscription && !userSubscription) {
      // Show premium popup if user doesn't have subscription
      notifier.warning("Siki AI requires a premium subscription");
      setShowPremiumPopup(true);
    } else if (item.path) {
      setActiveIndex(item.index);
      history.push(item.path);
    }
  };

 return (
  <Box sx={{ display: "flex" }}>
   <Box
  id="sideBarOuter"
  className="__sidebar_content"
  sx={{
    position: "fixed",
    left: 0,
    bottom: 0,
    width: "210px",
    zIndex: 10,
    boxShadow: "none",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
    backgroundColor: "#0d0d0d",
    transform: "translateZ(0)",
    willChange: "transform",
    overflowY: "auto", // enables scrolling for sidebar content
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: "60px",
      background: "linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0.8) 30%, rgba(0,0,0,0) 100%)",
      pointerEvents: "none",
      zIndex: 1,
    }
  }}
>
      {/* Menu Items */}
      <Box
        className="__menu_item"
        sx={{
          width: "100%",
          mt: 2, // Restored original margin
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: 1.5,
          pl: 2,
          pr: 2,
          pb: 4,
        }}
      >

<GoLiveButton />

<LeftBarUpload
  buttonSx={{
    width: "100%",
    justifyContent: "flex-start",
    alignItems: "center",
    display: "flex",
    textTransform: "none",
    padding: "12px 24px", // Match menu items
    borderRadius: "12px",  // Match menu items
    background: "transparent",
    border: "none",
    color: "#ffee20",
    fontWeight: 500, // Match normal menu items
    transition: "color 0.2s, background 0.2s, font-weight 0.2s",
    "&:hover": {
      background: "rgba(255,238,32,0.12)",
      color: "#fff700",
    },
  }}
  iconSx={{
    width: 22,          // Match menu item icons
    height: 22,         // Match menu item icons
    marginRight: "12px", // Match menu item icons (not 1.5)
    color: "inherit",
  }}
/>
        {/* Sleek Divider */}
  <Box
    sx={{
      width: "100%",
      height: "1px",
      background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.1) 20%, rgba(255,255,255,0.1) 80%, transparent)",
      my: 1.5, // margin top and bottom
    }}
  />

        {/* Render the rest of the menu items */}
        {isNavOpen &&
          menuItems.map((item) => (
            <Button
              key={item.index}
              className={`__item navButton ${activeIndex === item.index ? "navButton--active" : ""}`}
              onClick={() => handleMenuClick(item)}
              sx={{
                width: "100%",
                justifyContent: "flex-start",
                alignItems: "center",
                display: "flex",
                textTransform: "none",
                padding: "12px 24px",
                borderRadius: "12px",
                background: item.isPremium || (item.requiresSubscription && !userSubscription)
                  ? "linear-gradient(135deg, rgba(248,122,60,0.1) 0%, rgba(249,168,37,0.1) 100%)"
                  : activeIndex === item.index 
                    ? "rgba(255,238,32,0.07)" 
                    : "transparent",
                color: item.isPremium || (item.requiresSubscription && !userSubscription) ? "#F87A3C" : activeIndex === item.index ? "#ffee20" : "#fff",
                fontWeight: item.isPremium || activeIndex === item.index ? 700 : 500,
                transition: "color 0.2s, background 0.2s, font-weight 0.2s",
                border: item.isPremium ? "1px solid rgba(248,122,60,0.3)" : "none",
                "&:hover": {
                  background: item.isPremium
                    ? "linear-gradient(135deg, rgba(248,122,60,0.2) 0%, rgba(249,168,37,0.2) 100%)"
                    : "rgba(255,238,32,0.12)",
                  color: item.isPremium ? "#F9A825" : "#ffee20",
                  border: item.isPremium ? "1px solid rgba(248,122,60,0.5)" : "none",
                },
              }}
            >
              {item.isPremium ? (
                <Box
                  sx={{
                    width: 22,
                    height: 22,
                    marginRight: '12px',
                    display: 'inline-block',
                    verticalAlign: 'middle',
                    background: `url(${PremiumBadgeIcon}) no-repeat center`,
                    backgroundSize: 'contain',
                    filter: 'brightness(0) saturate(100%) invert(65%) sepia(85%) saturate(800%) hue-rotate(350deg) brightness(95%) contrast(95%)',
                  }}
                />
              ) : (
                <img
                  src={activeIndex === item.index ? item.activeImage : item.image}
                  alt={item.label}
                  className="navIcon"
                  style={{
                    width: 22,
                    height: 22,
                    marginRight: 12,
                    objectFit: "contain",
                    display: "inline-block",
                    verticalAlign: "middle",
                  }}
                />
              )}
              <Typography
                variant="body1"
                sx={{
                  color: "inherit",
                  fontWeight: "inherit",
                  fontSize: "1rem",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                {item.label}
                {item.requiresSubscription && !userSubscription && (
                  <LockIcon sx={{ fontSize: 16, color: "#F87A3C" }} />
                )}
              </Typography>
            </Button>
          ))}
      </Box>
    </Box>

    {/* Popups */}
    <AddPopup
      api="playlist"
      title={t("addPopup.title1")}
      open={false}
      handleClose={() => {}}
    />
    <SubscriptionPopup
      isOpen={showPremiumPopup}
      onClose={() => {
        setShowPremiumPopup(false);
        // Re-check subscription status after closing popup
        const checkUserSubscription = async () => {
          const userId = getUserId();
          if (userId) {
            try {
              const response = await ApiService.GET_SUBSCRIPTIONS(userId);
              setUserSubscription(response.result && response.result.length > 0);
            } catch (error) {
              console.error("Error checking subscription:", error);
            }
          }
        };
        checkUserSubscription();
      }}
    />
  </Box>
);
}